import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMic, FiSquare } from 'react-icons/fi';
import { AudioRecorder, formatTime } from '../utils/audio';
import './RecordingUI.css';

const RecordingUI = ({ onRecordingComplete, onError }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPulsing, setIsPulsing] = useState(true);
  const [duration, setDuration] = useState(0);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const recorderRef = useRef(null);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const timerRef = useRef(null);

  //Initialize recorder
  useEffect(() => {
    recorderRef.current = new AudioRecorder();
    return () => {
      if (recorderRef.current) {
        recorderRef.current.cleanup();
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Draw waveform visualization
  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    const recorder = recorderRef.current;

    if (!canvas || !recorder) return;

    const ctx = canvas.getContext('2d');
    const data = recorder.getFrequencyData();

    if (!data) {
      animationRef.current = requestAnimationFrame(drawWaveform);
      return;
    }

    const width = canvas.width;
    const height = canvas.height;

    ctx.clearRect(0, 0, width, height);

    const barWidth = 4;
    const gap = 3;
    const barCount = Math.floor(width / (barWidth + gap));
    const step = Math.floor(data.length / barCount);

    ctx.fillStyle = '#dca879';

    for (let i = 0; i < barCount; i++) {
      const dataIndex = i * step;
      const value = data[dataIndex] || 0;
      const barHeight = (value / 255) * height * 0.8 + 4;

      const x = i * (barWidth + gap);
      const y = (height - barHeight) / 2;

      ctx.beginPath();
      ctx.roundRect(x, y, barWidth, barHeight, 2);
      ctx.fill();
    }

    animationRef.current = requestAnimationFrame(drawWaveform);
  }, []);

  const startRecording = async () => {
    try {
      setPermissionDenied(false);
      await recorderRef.current.startRecording();
      setIsRecording(true);
      setIsPulsing(false);
      setDuration(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);

      // Start waveform visualization
      drawWaveform();
    } catch (error) {
      console.error('Recording error:', error);
      setPermissionDenied(true);
      onError?.(error.message);
    }
  };

  const stopRecording = async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    const audioBlob = await recorderRef.current.stopRecording();
    setIsRecording(false);
    setIsPulsing(true);

    if (audioBlob && audioBlob.size > 0) {
      onRecordingComplete?.(audioBlob);
    } else {
      onError?.('No audio recorded. Please try again.');
    }
  };

  const handleMicClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <div className="recording-ui">
      {/* Microphone button */}
      <motion.button
        className={`mic-button ${isRecording ? 'recording' : ''}`}
        onClick={handleMicClick}
        whileTap={{ scale: 0.95 }}
        animate={isPulsing ? {
          scale: [1, 1.05, 1],
          boxShadow: [
            '0 0 0 0 rgba(220, 168, 121, 0.4)',
            '0 0 0 20px rgba(220, 168, 121, 0)',
            '0 0 0 0 rgba(220, 168, 121, 0)'
          ]
        } : {}}
        transition={isPulsing ? {
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        } : {}}
      >
        <FiMic className="mic-icon" />
      </motion.button>

      {/* Timer */}
      <AnimatePresence>
        {isRecording && (
          <motion.div
            className="timer"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <span className="timer-dot"></span>
            {formatTime(duration)}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Waveform */}
      <div className={`waveform-container ${isRecording ? 'visible' : ''}`}>
        <canvas
          ref={canvasRef}
          width={300}
          height={60}
          className="waveform-canvas"
        />
      </div>

      {/* Hint text */}
      <AnimatePresence mode="wait">
        {!isRecording && !permissionDenied && (
          <motion.p
            className="hint-text"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            Click to start recording
          </motion.p>
        )}
        {permissionDenied && (
          <motion.p
            className="hint-text error"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            Please allow microphone access to record
          </motion.p>
        )}
      </AnimatePresence>

      {/* Done button */}
      <AnimatePresence>
        {isRecording && (
          <motion.button
            className="done-button"
            onClick={stopRecording}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FiSquare className="stop-icon" />
            Done
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RecordingUI;
